import { defineStore } from 'pinia';
import EGS from '@/util/egs_util.ts';
import dayjs from 'dayjs';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';
import type { AssigningMatch, NumericLike } from '@/types/assigning';
import { isHiddenMatchVisibility } from '@/util/matchAssignmentStatus';
import { getUserPreferences } from '@/composables/useUserPreferences';
import { parseUserDate } from '@/util/userDate';

interface MatchReportRef {
	matchNumber?: NumericLike;
}

export interface MyMatchesState {
	reportsDue: AssigningMatch[];
	upcoming: AssigningMatch[];
}

interface MyMatchesFetchOptions {
	commit?: boolean;
	includeHidden?: boolean;
}

interface MatchesStoreState {
	matches: AssigningMatch[];
	assignorMatches: AssigningMatch[];
	myMatches: MyMatchesState;
	groupsMatches: AssigningMatch[];
	groupMatches: AssigningMatch[];
	exportMatches: AssigningMatch[];
}

type MatchFilterCallback = ((matches: AssigningMatch[]) => AssigningMatch[]) | null;
type MatchesQueryParams = object;
type PositionUpdateDTO = Record<string, unknown>;

/** Payload accepted by PATCH /matches/edit/matches. */
export interface UpdateFootballMatchesRequest {
	matchNumber: NumericLike;
	group?: string;
	tempMatch: Partial<AssigningMatch>;
}

interface MyMatchesResponse {
	reportsDue?: AssigningMatch[];
	upcoming?: AssigningMatch[];
}

interface PositionUpdateResponse extends Record<string, unknown> {
	centerStatus?: string;
	ar1Status?: string;
	ar2Status?: string;
	fourthStatus?: string;
	mentorStatus?: string;
}

export interface AssignmentRestrictionViolation {
	restriction: string;
	message: string;
	limit: number | null;
	current: number | null;
}

export interface AssignmentEligibility extends Record<string, unknown> {
	matchNumber: NumericLike | null;
	officialNumber: NumericLike | null;
	position: string | null;
	selfAssignmentAllowed: boolean;
	violations: AssignmentRestrictionViolation[];
}

const user = JSON.parse(sessionStorage.getItem('user_info') || 'null') as { userNumber?: NumericLike } | null;
let latestCommittedMyMatchesRequest = 0;

function matchDateFormat(): string {
	const format = getUserPreferences().dateFormat;
	if (format) return format;
	try {
		const cached = JSON.parse(localStorage.getItem('user_settings') || 'null') as { dateFormat?: string } | null;
		return cached?.dateFormat || 'us-long';
	} catch {
		return 'us-long';
	}
}

function normalizeMatchDate(match: AssigningMatch, dateFormat = matchDateFormat()): AssigningMatch {
	const date = parseUserDate(match.matchDate, dateFormat);
	return date.isValid() ? { ...match, matchDate: date.format('YYYY-MM-DD') } : match;
}

function normalizeMatchDates(matches: AssigningMatch[] | undefined): AssigningMatch[] {
	const dateFormat = matchDateFormat();
	return Array.isArray(matches) ? matches.map((match) => normalizeMatchDate(match, dateFormat)) : [];
}

function getComparableTime(value: unknown): number {
	if (value instanceof Date) return value.getTime();
	if (typeof value === 'number') return value;
	if (typeof value === 'string' && value) {
		const parsed = Date.parse(value);
		return Number.isFinite(parsed) ? parsed : Number.NaN;
	}
	return Number.NaN;
}

function toPathSegment(value: NumericLike): string {
	return String(value ?? '');
}

function isSameMatch(match: AssigningMatch, matchId: NumericLike): boolean {
	const target = Number(matchId);
	const numericMatchNumber = Number(match.matchNumber);
	const numericId = Number((match as { id?: NumericLike }).id);
	return numericMatchNumber === target || numericId === target;
}

function removeMatchFromList(matches: AssigningMatch[], matchId: NumericLike): AssigningMatch[] {
	return matches.filter((match) => !isSameMatch(match, matchId));
}

export const useMatchesStore = defineStore('matchesStore', {
	state: (): MatchesStoreState => ({
		matches: [],
		assignorMatches: [],
		myMatches: {
			reportsDue: [],
			upcoming: []
		},
		groupsMatches: [],
		groupMatches: [],
		exportMatches: []
	}),
	actions: {
		async fetchAssignmentEligibility(matchNumber: NumericLike, officialNumber: NumericLike, position: string): Promise<AssignmentEligibility> {
			const normalizedPosition = position.trim().toUpperCase();
			const response = await apiClient.get<AssignmentEligibility>(`/matches/${toPathSegment(matchNumber)}/assignment-eligibility/${toPathSegment(officialNumber)}/${toPathSegment(normalizedPosition)}`);
			return response.data;
		},

		async fetchAndProcessMatches(url: string, filterCallback: MatchFilterCallback = null, filterPastMatches = true, submittedMatchReports: MatchReportRef[] = [], myMatchCall = false, data: unknown = null): Promise<AssigningMatch[]> {
			const res = data ? await apiClient.post<AssigningMatch[]>(url, data, { headers: { 'Content-Type': 'application/json' } }) : await apiClient.get<AssigningMatch[]>(url);
			if (res.status === 204 || !Array.isArray(res.data) || res.data.length === 0) {
				return [];
			}

			let obj = normalizeMatchDates(res.data);
			const submittedMatchNumbers = submittedMatchReports.map((report) => Number(report.matchNumber)).filter((matchNumber) => Number.isFinite(matchNumber));
			const userSettings = JSON.parse(localStorage.getItem('user_settings') || '{}') as { dateFormat?: string; timeFormat?: string };
			const dateFormat = userSettings.dateFormat || 'us-long';
			const timeFormat = userSettings.timeFormat || '12 HOUR';

			obj = obj.map((row) => {
				const matchDate = row.matchDate instanceof Date ? dayjs(row.matchDate).format('YYYY-MM-DD') : row.matchDate;

				const matchTime = row.matchTime instanceof Date ? dayjs(row.matchTime).format('HH:mm:ss') : row.matchTime;

				const formattedDate = EGS.util.formatDate(matchDate, dateFormat);
				const formattedTime = EGS.util.formatTime(matchTime, timeFormat);
				const dateTimeString = `${matchDate ?? ''}T${EGS.util.formatTime(matchTime, '24 HOUR')}`;
				const matchDateTime = dayjs(dateTimeString).toDate();

				row.formattedDate = formattedDate;
				row.formattedTime = formattedTime;
				row.dateTime = matchDateTime;

				return row;
			});

			if (filterCallback) obj = filterCallback(obj);

			if (filterPastMatches) {
				const now = dayjs().startOf('day').toDate();
				const nowTime = now.getTime();
				return obj.filter((match) => {
					const matchDateTime = getComparableTime(match.dateTime);
					const isFutureMatch = Boolean(match.matchDate) && matchDateTime >= nowTime;
					const matchNumber = Number(match.matchNumber);
					const isPastDueWithoutReport = matchDateTime < nowTime && !submittedMatchNumbers.includes(matchNumber);
					return myMatchCall ? isFutureMatch || isPastDueWithoutReport : isFutureMatch;
				});
			}

			return obj.sort((a, b) => getComparableTime(a.matchDate) - getComparableTime(b.matchDate));
		},

		async fetchAllMatches() {
			if (shouldThrottle('fetchAllMatches', 100)) {
				posthog.capture('throttled_action', { action: 'fetchAllMatches' });
				return;
			}
			const filterCallback = (obj: AssigningMatch[]) =>
				obj
					.filter((row) => row.status !== 'Canceled')
					.filter((row) => {
						const assignedPositions = [row.center, row.ar1, row.ar2, row.fourth, row.mentor];
						return !assignedPositions.some((position) => Number(position?.userNumber) === Number(user?.userNumber));
					});
			try {
				this.matches = await this.fetchAndProcessMatches('/matches/all', filterCallback, false);
			} catch {
				this.matches = [];
			}
		},

		async fetchMyMatches(params: MatchesQueryParams = {}, options: MyMatchesFetchOptions = {}): Promise<MyMatchesState> {
			const shouldCommit = options.commit !== false;
			const includeHidden = options.includeHidden === true;
			const requestId = shouldCommit ? ++latestCommittedMyMatchesRequest : 0;
			const empty: MyMatchesState = { reportsDue: [], upcoming: [] };
			try {
				const requestParams = includeHidden ? params : { ...params, visibilityFilter: 'public' };
				const res = await apiClient.get<MyMatchesResponse>('/matches/my-matches', { params: requestParams });
				const visibleMatches = (matches: AssigningMatch[] | undefined): AssigningMatch[] => {
					const normalized = normalizeMatchDates(matches);
					return includeHidden ? normalized : normalized.filter((match) => !isHiddenMatchVisibility(match.visibility));
				};
				const snapshot: MyMatchesState = {
					reportsDue: visibleMatches(res.data.reportsDue),
					upcoming: visibleMatches(res.data.upcoming)
				};
				if (shouldCommit && requestId === latestCommittedMyMatchesRequest) this.myMatches = snapshot;
				return snapshot;
			} catch {
				if (shouldCommit && requestId === latestCommittedMyMatchesRequest) this.myMatches = empty;
				return empty;
			}
		},

		async fetchMatchesByGroups(groupNumbers: number[]) {
			if (shouldThrottle('fetchMatchesByGroup', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMatchesByGroups' });
				return;
			}
			try {
				return await this.fetchAndProcessMatches('/matches/group/matches', null, true, [], false, groupNumbers);
			} catch {
				return [];
			}
		},

		async fetchGroupMatches(groupIds: NumericLike | NumericLike[] = [], params: MatchesQueryParams = {}): Promise<AssigningMatch[]> {
			if (shouldThrottle('fetchGroupMatches', 100)) {
				posthog.capture('throttled_action', { action: 'fetchGroupMatches' });
				return [];
			}
			try {
				const ids = Array.isArray(groupIds) ? groupIds.map((value) => Number(value)).filter(Boolean) : [Number(groupIds)].filter(Boolean);
				const res = await apiClient.post<AssigningMatch[]>('matches/group/matches', ids, { params });
				this.groupsMatches = normalizeMatchDates(res.data);
				return this.groupsMatches;
			} catch {
				this.groupsMatches = [];
				return [];
			}
		},

		async fetchMatchesByClient(clientNumbers: number[], params: MatchesQueryParams = {}): Promise<AssigningMatch[]> {
			if (shouldThrottle('fetchMatchesByClient', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMatchesByClient' });
				return [];
			}
			try {
				const { data } = await apiClient.post<AssigningMatch[]>('/matches/client/matches', clientNumbers, { params });
				this.assignorMatches = normalizeMatchDates(data);
				return this.assignorMatches;
			} catch {
				this.assignorMatches = [];
				return [];
			}
		},

		async fetchMatchByGroupAndGroupMatchNumber(groupNumber: NumericLike, groupMatchNumber: NumericLike): Promise<AssigningMatch | undefined> {
			if (shouldThrottle('fetchMatchByGroupAndGroupMatchNumber', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMatchByGroupAndGroupMatchNumber' });
				return;
			}
			try {
				const response = await apiClient.get<AssigningMatch>(`/matches/group/${toPathSegment(groupNumber)}/match/${toPathSegment(groupMatchNumber)}`);
				return normalizeMatchDate(response.data);
			} catch {}
		},

		async fetchMatchByMatchNumber(matchNumber: NumericLike): Promise<AssigningMatch | undefined> {
			if (shouldThrottle('fetchMatchByMatchNumber', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMatchByMatchNumber' });
				return;
			}
			try {
				const response = await apiClient.get<AssigningMatch>(`/matches/${toPathSegment(matchNumber)}`);
				return normalizeMatchDate(response.data);
			} catch {}
		},

		async fetchMatchesByUser(userNumber: NumericLike): Promise<AssigningMatch[] | undefined> {
			if (shouldThrottle('fetchMatchesByUser', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMatchesByUser' });
				return;
			}
			try {
				return await this.fetchAndProcessMatches(`/matches/user/${toPathSegment(userNumber)}`, null, false, [], false);
			} catch {
				return undefined;
			}
		},

		async selfAssignToMatch(matchNumber: NumericLike, position: string) {
			if (shouldThrottle('selfAssignToMatch', 250)) {
				posthog.capture('throttled_action', { action: 'selfAssignToMatch' });
				return null;
			}
			const pos = position.toUpperCase();
			return await apiClient.post(`/matches/${toPathSegment(matchNumber)}/self-assign/${pos}`);
		},

		async saveMatch(match: AssigningMatch) {
			if (shouldThrottle('saveMatch', 100)) {
				posthog.capture('throttled_action', { action: 'saveMatch' });
				return;
			}
			const groupSource = match.group || match.client?.groupId || null;
			const group = groupSource ? { groupNumber: groupSource.groupNumber } : null;
			const payload = {
				...match,
				group,
				matchDate: EGS.util.formatSaveDate(match.matchDate),
				matchTime: EGS.util.formatSaveTime(match.matchTime)
			};
			await apiClient.post('/matches/save', payload);
		},

		async updatePosition(matchNumber: NumericLike, positionDTO: PositionUpdateDTO) {
			if (shouldThrottle('updatePosition', 100)) {
				posthog.capture('throttled_action', { action: 'updatePosition' });
				return;
			}
			const { data } = await apiClient.patch<PositionUpdateResponse>(`/matches/edit/${toPathSegment(matchNumber)}/position`, positionDTO);
			return data;
		},

		async updateMatch(match: AssigningMatch) {
			if (shouldThrottle('updateMatch', 100)) {
				posthog.capture('throttled_action', { action: 'updateMatch' });
				return;
			}
			const dto = EGS.MatchDTO.fullMatch(match as unknown as Parameters<typeof EGS.MatchDTO.fullMatch>[0]) as unknown as AssigningMatch;
			dto.matchDate = EGS.util.formatSaveDate(dto.matchDate);
			dto.matchTime = EGS.util.formatSaveTime(dto.matchTime);
			await apiClient.patch(`/matches/edit/${toPathSegment(dto.matchNumber)}`, dto);
		},
		async updateMatchByGroupNumber(groupNumber: NumericLike, groupMatchNumber: NumericLike, mergePatch: Partial<AssigningMatch>): Promise<AssigningMatch | undefined> {
			if (shouldThrottle('updateMatchByGroupNumber', 100)) {
				posthog.capture('throttled_action', {
					action: 'updateMatchByGroupNumber'
				});
				return;
			}
			if (!groupNumber || !groupMatchNumber) {
				throw new Error('groupNumber and groupMatchNumber are required to update a match');
			}
			const payload: Record<string, unknown> = {
				...mergePatch
			};
			// Path variables / relational DTOs should not be sent in the merge-patch body.
			delete payload['group'];
			delete payload['groupNumber'];
			delete payload['groupMatchNumber'];
			delete payload['matchNumber'];
			delete payload['client'];
			delete payload['location'];
			delete payload['fieldEntity'];
			delete payload['center'];
			delete payload['ar1'];
			delete payload['ar2'];
			delete payload['fourth'];
			delete payload['mentor'];
			if (payload['matchDate']) {
				payload['matchDate'] = EGS.util.formatSaveDate(payload['matchDate']);
			}
			if (payload['matchTime']) {
				payload['matchTime'] = EGS.util.formatSaveTime(payload['matchTime']);
			}
			const { data } = await apiClient.patch<AssigningMatch>(`/matches/edit/group/${toPathSegment(groupNumber)}/match/${toPathSegment(groupMatchNumber)}`, payload, {
				headers: {
					'Content-Type': 'application/merge-patch+json'
				}
			});
			return normalizeMatchDate(data);
		},
		async updateMatches(updateRequests: UpdateFootballMatchesRequest[]) {
			if (shouldThrottle('updateMatches', 100)) {
				posthog.capture('throttled_action', { action: 'updateMatches' });
				return;
			}
			const response = await apiClient.patch('/matches/edit/matches', updateRequests, {
				headers: {
					'Content-Type': 'application/merge-patch+json'
				}
			});
			return response;
		},
		async uploadMatches(matchData: AssigningMatch[]) {
			if (shouldThrottle('uploadMatches', 100)) {
				posthog.capture('throttled_action', { action: 'uploadMatches' });
				return;
			}
			return await apiClient.post('/matches/saveAll', matchData);
		},

		async deleteMatch(matchId: NumericLike) {
			if (shouldThrottle('deleteMatch', 100)) {
				posthog.capture('throttled_action', { action: 'deleteMatch' });
				return;
			}
			await apiClient.delete(`/matches/${toPathSegment(matchId)}`);
			this.matches = removeMatchFromList(this.matches, matchId);
			this.assignorMatches = removeMatchFromList(this.assignorMatches, matchId);
			this.groupsMatches = removeMatchFromList(this.groupsMatches, matchId);
			this.groupMatches = removeMatchFromList(this.groupMatches, matchId);
			this.exportMatches = removeMatchFromList(this.exportMatches, matchId);
			this.myMatches = {
				reportsDue: removeMatchFromList(this.myMatches.reportsDue, matchId),
				upcoming: removeMatchFromList(this.myMatches.upcoming, matchId)
			};
		},

		setExportMatches(matches: AssigningMatch[]) {
			this.exportMatches = matches;
		}
	}
});
